import { find, findIndex, sortBy } from 'lodash';
import React from 'react';
import { cacheItem, getCachedItem } from '../../../../util/cache';
import { getFormatter, getHeaderRenderer } from './defaults';

export const useColumnDef = (gridName, columnDef, rowKeyPath) => {
  const localStorageKey = `${gridName?.toLowerCase?.()?.replace(/ /g, '-')}-grid-prefs`;

  // Apply defaults to each column.
  const preparedColumnDefs = React.useMemo(() => {
    return columnDef.map(({ filterable, ...col }) => ({
      ...col,
      headerRenderer: col.headerRenderer || getHeaderRenderer(filterable, col),
      formatter: col.formatter || getFormatter(col, rowKeyPath),
    }));
  }, [columnDef, rowKeyPath]);

  const [userColumns, setUserColumns] = React.useState(preparedColumnDefs);

  React.useEffect(() => {
    const preferences = JSON.parse(getCachedItem(localStorageKey));

    if (preferences) {
      const preferredVisibilityApplied = preparedColumnDefs.map(columnDef => {
        const userColumn = find(preferences, { key: columnDef.key });
        return { ...columnDef, hidden: userColumn?.hidden ?? columnDef.hidden };
      });

      const preferredSortingApplied = sortBy(preferredVisibilityApplied, col =>
        findIndex(preferences, pref => col.key === pref.key)
      );

      setUserColumns(preferredSortingApplied);
    } else {
      setUserColumns(preparedColumnDefs);
    }
  }, [localStorageKey, preparedColumnDefs]);

  const saveColumnPreferences = React.useCallback(
    cols => {
      cacheItem(localStorageKey, JSON.stringify(cols));

      const mergedColumns = cols.map(column => {
        const originalColumn = find(preparedColumnDefs, { key: column.key });
        return { ...originalColumn, hidden: column.hidden };
      });

      setUserColumns(mergedColumns);
    },
    [localStorageKey, preparedColumnDefs]
  );

  const visibleColumns = React.useMemo(() => {
    return userColumns.filter(c => !c.hidden);
  }, [userColumns]);

  return { visibleColumns, userColumns, saveColumnPreferences, columnDef };
};
